import { useAuth0 } from "@auth0/auth0-react";

import { DASHBOARD_RETURN_TO } from "content/landing";
import { CtaLocation, trackCtaClick, trackSignupStarted } from "lib/analytics";
import { SubscriptionPlanEnum } from "types/subscription/SubscriptionPlanEnum";
import { buildPlanCheckoutPath } from "utils/subscription/planCheckoutPath";

/**
 * Build the post-login `returnTo` path for a given tier.
 * Paid tiers land on the billing page with `?plan=<tierId>` so the
 * auto-checkout hook can pick it up. Free / undefined land on the dashboard root.
 */
function buildReturnTo(tierId?: SubscriptionPlanEnum): string {
  if (tierId && tierId !== SubscriptionPlanEnum.FREE) {
    return buildPlanCheckoutPath(tierId);
  }
  return DASHBOARD_RETURN_TO;
}

export function useLandingAuthActions() {
  const { loginWithRedirect } = useAuth0();

  /** Generic login (no plan selection). */
  const login = async (location?: CtaLocation) => {
    if (location) {
      trackCtaClick(location);
    }
    await loginWithRedirect({
      appState: { returnTo: DASHBOARD_RETURN_TO },
    });
  };

  /** Generic signup (no plan selection). Flags isNewSignup so the Auth0 callback lands the user in the create-project wizard. */
  const signup = async (location?: CtaLocation, returnTo?: string) => {
    if (location) {
      trackCtaClick(location);
      trackSignupStarted(location);
    }
    await loginWithRedirect({
      authorizationParams: { screen_hint: "signup" },
      appState: { returnTo: returnTo ?? DASHBOARD_RETURN_TO, isNewSignup: true },
    });
  };

  /** Signup with a pre-selected plan. Paid tiers redirect to billing + Stripe checkout after Auth0; only free tier flags isNewSignup. */
  const signupWithPlan = async (params: { tierId: SubscriptionPlanEnum; location?: CtaLocation }) => {
    const { tierId, location } = params;
    if (location) {
      trackCtaClick(location);
      trackSignupStarted(location);
    }
    const isFreeTier = tierId === SubscriptionPlanEnum.FREE;
    await loginWithRedirect({
      authorizationParams: { screen_hint: "signup" },
      appState: { returnTo: buildReturnTo(tierId), ...(isFreeTier && { isNewSignup: true }) },
    });
  };

  return { login, signup, signupWithPlan, buildReturnTo };
}
